import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Send, Sparkles, Loader2 } from 'lucide-react';
import { runNlqPipeline, type NlqResult, type PipelineStage } from '../engine/NlqEngine';
import { buildSearchIndex } from '../search/SearchIndex';
import { ResultSummaryCard } from './ResultSummaryCard';
import { ResultsOverlay } from './ResultsOverlay';
import { FeedbackBar } from '../../components/FeedbackBar';
import type { Request, Touchpoint } from '../../types';
interface Props {
  requests: Request[];
  touchpoints: Touchpoint[];
  onError: (msg: string) => void;
}
interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  result?: NlqResult;
  timestamp: number;
}
const STORAGE_KEY = 'ai-chat-nlq';
const ALL_SUGGESTIONS = ['Total GNARR by region', 'Average age of requests by consultant', 'Count requests by status', 'Top 10 consultants by GNARR', "What's the min and max GNARR per region", 'Total GNARR and average age by region', 'Sum and average of GNARR by consultant', 'Total GNARR for requests in EMEA', 'Average age of closed requests', 'Show Enterprise requests over 50000 GNARR', 'Count of In Progress requests by consultant', 'Show everything for Example Manufacturing', 'Show all activity for Solution Consultant X', 'Show requests and touchpoints in JAPAC', 'Show touchpoints mentioning roadmap', 'Show touchpoints mentioning migration', 'Top 5 requests by GNARR', 'Show the oldest 10 open requests', 'Show closed requests in EMEA sorted by GNARR descending', 'Top 10 consultants by GNARR in JAPAC', 'Show Enterprise requests over 500000 GNARR in AMER'];
function pickRandom<T>(arr: T[], count: number): T[] {
  const shuffled = [...arr].sort(() => Math.random() - 0.5);
  return shuffled.slice(0, count);
}
function uid(): string {
  return Date.now().toString(36) + Math.random().toString(36).slice(2, 8);
}
function loadHistory(): ChatMessage[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed.messages) ? parsed.messages : [];
  } catch {
    return [];
  }
}
function saveHistory(messages: ChatMessage[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({
      messages
    }));
  } catch {/* quota exceeded */}
}
const GREETING: ChatMessage = {
  id: 'greeting',
  role: 'assistant',
  content: "Hi! I'm the OnDemand AI. Ask me about your requests and touchpoints.",
  timestamp: 0
};
const STAGE_LABELS: Record<PipelineStage, string> = {
  planning: 'Generating query...',
  validating: 'Validating...',
  executing: 'Executing...',
  explaining: 'Summarizing...',
  complete: 'Done',
  error: 'Error'
};
export function NlqChatPanel({
  requests,
  touchpoints,
  onError
}: Props) {
  const stored = loadHistory();
  const [messages, setMessages] = useState<ChatMessage[]>(stored.length > 0 ? stored : [GREETING]);
  const [input, setInput] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [stage, setStage] = useState<PipelineStage | null>(null);
  const [dataReady, setDataReady] = useState(false);
  const [indexReady, setIndexReady] = useState(false);
  const [overlayResult, setOverlayResult] = useState<NlqResult | null>(null);
  const [suggestionKey, setSuggestionKey] = useState(0);
  const suggestions = useMemo(() => pickRandom(ALL_SUGGESTIONS, 5), [suggestionKey]);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const abortRef = useRef<AbortController | null>(null);
  useEffect(() => {
    if (requests.length > 0 || touchpoints.length > 0) {
      setDataReady(true);
      buildSearchIndex(requests as unknown as Record<string, unknown>[], touchpoints as unknown as Record<string, unknown>[]);
      setIndexReady(true);
    }
  }, [requests, touchpoints]);
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({
      behavior: 'smooth'
    });
  }, [messages]);
  useEffect(() => {
    saveHistory(messages);
  }, [messages]);
  const handleSend = useCallback(async () => {
    const text = input.trim();
    if (!text || isProcessing || !dataReady || !indexReady) return;
    setSuggestionKey(k => k + 1);
    const userMsg: ChatMessage = {
      id: uid(),
      role: 'user',
      content: text,
      timestamp: Date.now()
    };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setIsProcessing(true);
    const assistantId = uid();
    const assistantMsg: ChatMessage = {
      id: assistantId,
      role: 'assistant',
      content: '',
      timestamp: Date.now()
    };
    setMessages(prev => [...prev, assistantMsg]);
    const controller = new AbortController();
    abortRef.current = controller;
    try {
      const result = await runNlqPipeline(text, requests as unknown as Record<string, unknown>[], touchpoints as unknown as Record<string, unknown>[], s => setStage(s), controller.signal);
      let responseContent = '';
      if (result.error) {
        responseContent = result.suggestion ? `I couldn't convert your question to a query. ${result.suggestion}` : `I couldn't process your question: ${result.error}`;
      } else if (result.explanation) {
        responseContent = result.explanation;
      } else if (result.results) {
        responseContent = `Found ${result.results.metadata.totalRows} results.`;
      }
      setMessages(prev => prev.map(m => m.id === assistantId ? {
        ...m,
        content: responseContent,
        result
      } : m));
    } catch (err: unknown) {
      console.error('[NLQ] handleSend error:', err);
      if (err instanceof Error && err.name === 'AbortError') return;
      const msg = err instanceof Error ? err.message : 'Unknown error';
      onError(msg);
      setMessages(prev => prev.filter(m => m.id !== assistantId));
    } finally {
      setIsProcessing(false);
      setStage(null);
      abortRef.current = null;
      inputRef.current?.focus();
    }
  }, [input, isProcessing, dataReady, indexReady, requests, touchpoints, onError]);
  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };
  const isDisabled = isProcessing || !dataReady || !indexReady;
  return <>
      {/* Status indicator */}
      {(!dataReady || !indexReady) && <div className="ai-status-bar" data-id="element-994" data-mp="2mf4vy69catpy">
          <Loader2 size={12} className="ai-spin" data-id="element-995" data-mp="2me23vv0a6l8g" />
          <span data-id="element-996" data-mp="160v8rfgu3b0p">Loading data and building search index...</span>
        </div>}

      {/* Messages */}
      <div className="ai-messages" data-id="element-997" data-mp="1azonfb7ux82g">
        {messages.map(msg => <div key={msg.id} className={`ai-msg ai-msg-${msg.role}`} data-id="element-998" data-mp="1axvdnn1y67q8">
            {msg.role === 'assistant' && <div className="ai-msg-avatar" data-id="element-999" data-mp="13i9xeruz4tfd">
                <Sparkles size={12} data-id="element-1000" data-mp="ygaxxt2cugnp" />
              </div>}
            <div className="ai-msg-bubble" data-id="element-1001" data-mp="1cgvhwsk0aiis">
              {msg.role === 'assistant' ? <div className="ai-md" data-id="element-1002" data-mp="17iadegxq1coj">
                  {msg.content ? <ReactMarkdown remarkPlugins={[remarkGfm]} data-id="element-1003" data-mp="17fy1olik3yjx">{msg.content}</ReactMarkdown> : isProcessing && msg.id === messages[messages.length - 1]?.id ? <div className="ai-pipeline-status" data-id="element-1004" data-mp="fsflqdme5awp">
                      {stage && <span className="ai-pipeline-label" data-id="element-1005" data-mp="tij7l3hcz2lm">{STAGE_LABELS[stage]}</span>}
                      <div className="ai-typing" data-id="element-1006" data-mp="or12c38ayfuu">
                        <span data-id="element-1007" data-mp="oq81tjzxsml5" /><span data-id="element-1008" data-mp="opg8ygxq52yk" /><span data-id="element-1009" data-mp="oppy62ay3637" />
                      </div>
                    </div> : null}
                </div> : <span data-id="element-1010" data-mp="3s7894im0kt7u">{msg.content}</span>}
              {msg.result?.results && <ResultSummaryCard result={msg.result} onExpand={() => setOverlayResult(msg.result!)} data-id="element-1011" data-mp="37nhixr6730v2" />}
              {msg.role === 'assistant' && msg.content && <FeedbackBar messageId={msg.id} query={messages.find((m, i) => m.role === 'user' && i < messages.indexOf(msg) && messages[i + 1]?.id === msg.id)?.content} mode="nlq" data-id="element-1012" data-mp="3gp7acx02tzsu" />}
            </div>
          </div>)}

        {!isProcessing && !input && messages.length <= 1 && <div className="ai-suggestions" data-id="element-1013" data-mp="39j1ea6bnqom">
            {suggestions.map(s => <button key={s} className="ai-suggestion-chip" onClick={() => {
          setInput(s);
          inputRef.current?.focus();
        }} data-id="element-1014" data-mp="869govaayi8z">
                {s}
              </button>)}
          </div>}

        <div ref={messagesEndRef} data-id="element-1015" data-mp="22p46y760070t" />
      </div>

      {/* Input */}
      <div className="ai-input-bar" data-id="element-1016" data-mp="1xo6p5vowhezu">
        <textarea ref={inputRef} className="ai-input" placeholder={!dataReady ? 'Loading data...' : !indexReady ? 'Building search index...' : 'Ask about your requests and touchpoints...'} value={input} onChange={e => setInput(e.target.value)} onKeyDown={handleKeyDown} rows={1} disabled={isDisabled} data-id="element-1017" data-mp="1xn518qmscy0p" />
        <button className="ai-send-btn" onClick={() => void handleSend()} disabled={!input.trim() || isDisabled} title="Send message" data-id="element-1018" data-mp="2bn64udufqjjv">
          {isProcessing ? <Loader2 size={16} className="ai-spin" data-id="element-1019" data-mp="345abimizpsbo" /> : <Send size={16} data-id="element-1020" data-mp="346d2svk85ws4" />}
        </button>
      </div>

      {/* Results overlay */}
      {overlayResult && overlayResult.results && <ResultsOverlay result={overlayResult} onClose={() => setOverlayResult(null)} data-id="element-1021" data-mp="16wsbff091ahb" />}
    </>;
}